import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Save, X, ShoppingCart, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/utils'
import { materialsApi, apiClient } from '@/services/api'
import { SupplierManager } from '@/components/materials/SupplierManager'

interface Material {
  id: string
  name: string
  sku: string
  baseUnit: string
  currentStock: number
  specifications?: {
    color?: string
    gauge?: string
    material?: string
    finish?: string
  }
}


interface PurchaseItem {
  materialId: string
  materialName: string
  packageQuantity: number      // How many packages/bags purchased
  itemsPerPackage: number      // How many individual items per package
  totalItems: number           // Total individual items (calculated)
  costPerPackage: number       // Cost per package/bag
  unitCost: number            // Cost per individual item (calculated)
  totalCost: number           // Total purchase cost
}

export const PurchasePage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [materials, setMaterials] = useState<Material[]>([])
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [formData, setFormData] = useState({
    supplierId: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    invoiceNumber: '',
    notes: ''
  })
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([
    {
      materialId: '',
      materialName: '',
      packageQuantity: 1,
      itemsPerPackage: 0,
      totalItems: 0,
      costPerPackage: 0,
      unitCost: 0,
      totalCost: 0
    }
  ])

  useEffect(() => {
    fetchMaterials()
  }, [])

  useEffect(() => {
    // Check if a specific material was pre-selected via URL params
    const materialId = searchParams.get('materialId')
    if (materialId && materials.length > 0) {
      const material = materials.find(m => m.id === materialId)
      if (material) {
        // Pre-select the material in the first purchase item
        setPurchaseItems(prev => {
          const newItems = [...prev]
          newItems[0].materialId = materialId
          newItems[0].materialName = material.name
          return newItems
        })
      }
    }
  }, [searchParams, materials])

  const fetchMaterials = async () => {
    try {
      const response = await materialsApi.getAll()
      setMaterials(Array.isArray(response) ? response : (response?.data || []))
    } catch (error) {
      console.error('Error fetching materials:', error)
      setMaterials([])
    } finally {
      setIsLoadingData(false)
    }
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const validItems = purchaseItems.filter(item => item.materialId && item.totalItems > 0)
      
      if (validItems.length === 0) {
        alert('Please add at least one valid purchase item.')
        return
      }

      // Record each purchase item separately to create proper FIFO batches
      for (const item of validItems) {
        const purchaseData = {
          materialId: item.materialId,
          quantity: item.totalItems || item.packageQuantity || 1, // Use total items for stock update
          itemsPerPackage: item.itemsPerPackage || 1,
          totalCost: item.totalCost || 0,
          costPerPackage: item.costPerPackage || 0,
          unitCost: item.unitCost || 0,
          totalItems: item.totalItems || 1,
          packageQuantity: item.packageQuantity || 1,
          supplierId: formData.supplierId || undefined,
          invoiceNumber: formData.invoiceNumber || undefined,
          notes: formData.notes || undefined,
          purchaseDate: formData.purchaseDate,
          isPackagePurchase: (item.itemsPerPackage || 0) > 0
        }

        await apiClient.post(`/materials/${item.materialId}/purchase`, purchaseData)
      }
      
      // Navigate back to materials list to see updated stock levels
      navigate('/materials')
    } catch (error) {
      console.error('Error recording purchase:', error)
      alert('Error recording purchase. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleItemChange = (index: number, field: keyof PurchaseItem, value: string | number) => {
    setPurchaseItems(prev => {
      const newItems = [...prev]
      newItems[index] = { ...newItems[index], [field]: value }
      
      // Update material name when material ID changes
      if (field === 'materialId') {
        const material = materials.find(m => m.id === value)
        newItems[index].materialName = material?.name || ''
      }
      
      // Recalculate derived values when package info changes
      if (field === 'packageQuantity' || field === 'itemsPerPackage' || field === 'totalCost') {
        const item = newItems[index]
        
        // Calculate total items
        item.totalItems = item.packageQuantity * item.itemsPerPackage
        
        // Calculate cost per package
        if (item.packageQuantity > 0 && item.totalCost > 0) {
          item.costPerPackage = item.totalCost / item.packageQuantity
        } else {
          item.costPerPackage = 0
        }
        
        // Calculate unit cost (cost per individual item)
        if (item.totalItems > 0 && item.totalCost > 0) {
          item.unitCost = item.totalCost / item.totalItems
        } else {
          item.unitCost = 0
        }
      }
      
      return newItems
    })
  }

  const addItem = () => {
    setPurchaseItems(prev => [...prev, {
      materialId: '',
      materialName: '',
      packageQuantity: 1,
      itemsPerPackage: 0,
      totalItems: 0,
      costPerPackage: 0,
      unitCost: 0,
      totalCost: 0
    }])
  }

  const removeItem = (index: number) => {
    if (purchaseItems.length > 1) {
      setPurchaseItems(prev => prev.filter((_, i) => i !== index))
    }
  }

  const totalPurchaseAmount = purchaseItems.reduce((sum, item) => sum + item.totalCost, 0)

  return (
    <>
      <Helmet>
        <title>Record Purchase - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/materials')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Materials
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Record Purchase
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Add new inventory batches with FIFO cost tracking
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Purchase Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Purchase Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Supplier */}
              <div>
                <SupplierManager
                  selectedSupplierId={formData.supplierId}
                  onSupplierChange={(supplierId: string) => setFormData(prev => ({ ...prev, supplierId }))}
                />
              </div>

              {/* Purchase Date */}
              <div>
                <label htmlFor="purchaseDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Purchase Date *
                </label>
                <input
                  type="date"
                  id="purchaseDate"
                  name="purchaseDate"
                  required
                  value={formData.purchaseDate}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                />
              </div>

              {/* Invoice Number */}
              <div>
                <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Number
                </label>
                <input
                  type="text"
                  id="invoiceNumber"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="e.g., INV-2024-001"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                placeholder="Any additional notes about this purchase..."
              />
            </div>
          </div>

          {/* Purchase Items */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Purchase Items
              </h2>
              <Button
                type="button"
                variant="outline"
                onClick={addItem}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add Item
              </Button>
            </div>

            <div className="space-y-4">
              {purchaseItems.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    {/* Material */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Material *
                      </label>
                      <select
                        required
                        value={item.materialId}
                        onChange={(e) => handleItemChange(index, 'materialId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                      >
                        <option value="">Select a material</option>
                        {materials.map((material) => (
                          <option key={material.id} value={material.id}>
                            {material.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Package Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Packages *
                      </label>
                      <input
                        type="number"
                        required
                        step="1"
                        min="1"
                        value={item.packageQuantity || ''}
                        onChange={(e) => handleItemChange(index, 'packageQuantity', parseFloat(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">How many bags/packages</p>
                    </div>

                    {/* Items Per Package */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Per Package *
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        value={item.itemsPerPackage || ''}
                        onChange={(e) => handleItemChange(index, 'itemsPerPackage', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Items in each package</p>
                    </div>

                    {/* Total Items (calculated) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Total Items
                      </label>
                      <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                        {item.totalItems}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Calculated total</p>
                    </div>

                    {/* Total Cost */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Total Cost ($) *
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        value={item.totalCost || ''}
                        onChange={(e) => handleItemChange(index, 'totalCost', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">What you paid</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-end">
                      {purchaseItems.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Calculated Values Display */}
                  {item.totalItems > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Cost per package:</span>
                          <span className="ml-2 font-medium">{formatCurrency(item.costPerPackage)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Cost per item:</span>
                          <span className="ml-2 font-medium">{formatCurrency(item.unitCost)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end">
                <div className="text-right">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Total Purchase Amount
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(totalPurchaseAmount)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/materials')}
              leftIcon={<X className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Record Purchase
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}